import bearing from '@turf/bearing'
import constants from '@/constants/url'
import { getHeaders, blobToBase64 } from '@/utils'

// load icons
const MAP_ICON_SIZE = 64
const events = [
  {
    name: 'sign-icon',
    url: require('@/assets/images/odd/sign-icon.svg')
  },
  {
    name: 'event-icon',
    url: require('@/assets/images/odd/event-icon.svg')
  },
  {
    name: 'work-icon',
    url: require('@/assets/images/odd/work-icon.svg')
  },
  {
    name: 'accident-icon',
    url: require('@/assets/images/odd/accident-icon.svg')
  },
  {
    name: 'play',
    url: require('@/assets/icons/mono/general/play.svg'),
    sdf: true
  },
  {
    name: 'aghk-bus-default',
    url: require('@/assets/images/monitoring/aghk-bus-default.svg'),
    size: 44
  }
]

const signSizeConfig = {
  h: ['8.1.1', '8.1.3', '8.1.4', '8.10', '8.11', '8.12', '8.14', '8.15', '8.16', '8.17', '8.18', '8.2.1', '8.2.5', '8.2.6', '8.20.1', '8.20.2', '8.21.1', '8.21.1', '8.21.2', '8.21.3', '8.3.1', '8.3.2', '8.3.3', '8.4.1', '8.4.2', '8.4.3', '8.4.4', '8.4.5', '8.4.6', '8.4.7', '8.4.8', '8.5.1', '8.5.2', '8.5.3', '8.5.4', '8.5.5', '8.5.6', '8.5.7', '8.6.1', '8.6.2', '8.6.3', '8.6.4', '8.6.5', '8.6.6', '8.6.7', '8.6.8', '8.6.9', '8.7', '8.8', '8.9'],
  v: ['8.2.2', '8.2.3', '8.2.4']
}

const getPromisesFromArray = (component, array) =>
  array.map(
    item =>
      new Promise(resolve => {
        const size = item.size || MAP_ICON_SIZE
        const image = new Image(size, size)
        image.crossOrigin = 'Anonymous'
        image.style.backgroundPosition = '50%, 50%'
        image.style.backgroundSize = '100%'
        image.style.backgroundColor = '#fff'
        image.addEventListener('load', () => {
          let _image = image

          const exceptions = ['8.19', '8.22.1', '8.22.2', '8.22.3']

          if (item.test?.charAt(0) === '8' && !exceptions?.includes(item.test)) {
            const hor = signSizeConfig.h.includes(item.test)
            const ver = signSizeConfig.v.includes(item.test)

            const canvas = document.createElement('canvas')
            canvas.width = size
            canvas.height = size
            const ctx = canvas.getContext('2d')

            ctx.fillStyle = '#fff'
            ctx.fillRect(0, 0, size, size)
            ctx.drawImage(image, 0, 0, size, size)

            if (hor) {
              _image = ctx.getImageData(0, size / 4, size, size / 2)
            } else if (ver) {
              _image = ctx.getImageData(size / 4, 0, size / 2, size)
            } else {
              _image = ctx.getImageData(0, 0, size, size)
            }
          }

          if (!component.mapgl.hasImage(item.name)) {
            component.mapgl.addImage(item.name, _image, { sdf: item.sdf })
          }
          resolve()
        })

        image.addEventListener('error', () => {
          console.log('odd icon not loaded: ', item.name)
          resolve()
        })

        image.src = item.url
      })
  )

export const loadIcons = async(component, signIcons) => {
  const icons = [...events]

  component.$store.commit('SET_ODD_FIELD', { field: 'iconsLoading', value: true })

  for (let i = 0; i < signIcons.length; i++) {
    const item = signIcons[i]
    const url = `${constants.URL}/download_resource/${item.resource_id}`
    const lsId = `icon-${item.resource_id}`
    const iconText = localStorage.getItem(lsId)
    let blob

    if (iconText === null) {
      const res = await fetch(url, { headers: getHeaders() })

      blob = await res.blob()
      blobToBase64(blob).then(base64 => {
        localStorage.setItem(lsId, base64)
      })
    } else {
      const base64Response = await fetch(iconText)

      blob = await base64Response.blob()
    }

    icons.push({
      name: item.resource_id,
      test: item.name,
      url: URL.createObjectURL(blob)
    })
  }

  const promises = getPromisesFromArray(component, icons)

  component.$store.commit('SET_ODD_FIELD', { field: 'iconsLoading', value: false })

  return Promise.all(promises)
    .then(() => console.log('odd icons loaded!'))
    .catch(() => console.log('odd icons not loaded'))
}

export const getSignsFeatures = features =>
  features.reduce((prev, current) => {
    current.projection = current.rack?.geom || null
    const items = []
    let angle = 0

    if (current.rack?.projection) {
      angle = bearing(current.rack?.projection, current.rack?.geom) - 90
    }

    items.push({
      type: 'Feature',
      geometry: current.rack?.geom || current.geom,
      properties: {
        id: current.id,
        type: 'symbols',
        position: current.rack_position,
        resource_id: current.sign_icon
          ? current.sign_icon.resource_id
          : 'sign-icon',

        layerType: 'signs',
        angle
      }
    })

    return [...prev, ...items]
  }, [])

export const getRackFeatures = features =>
  features.reduce((prev, current) => {
    const items = []

    if (!current.projection && current.signs?.length) {
      current.projection = current.signs.find(s => s.projection)?.projection || null
    }

    if (current.projection) {
      items.push({
        type: 'Feature',
        geometry: current.projection,
        properties: {
          id: current.id,
          type: 'points'
        }
      })
      items.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            current.projection.coordinates,
            current.geom.coordinates
          ]
        },
        properties: {
          id: current.id,
          layerType: 'line'
        }
      })
    }

    items.push({
      type: 'Feature',
      geometry: current.geom,
      properties: {
        id: current.id,
        type: 'symbols'
      }
    })

    return [...prev, ...items]
  }, [])
